import { Injectable, signal, computed } from '@angular/core';

/**
 * Stores the active organization selected by a Superadmin.
 * Persists to sessionStorage so it survives page navigation.
 */
@Injectable({ providedIn: 'root' })
export class ActiveOrgService {
  private static readonly STORAGE_KEY = 'activeOrganizationId';

  private _activeOrgId = signal<number | null>(this.loadFromStorage());

  /** Current active organization id (null = all organizations / none selected). */
  activeOrgId = computed(() => this._activeOrgId());

  /** Whether an organization has been selected. */
  hasSelection = computed(() => this._activeOrgId() !== null);

  /**
   * Set the active organization (Superadmin picks one).
   * Pass null to clear (show all organizations for view).
   */
  select(organizationId: number | null): void {
    this._activeOrgId.set(organizationId);

    if (organizationId !== null) {
      sessionStorage.setItem(ActiveOrgService.STORAGE_KEY, String(organizationId));
    } else {
      sessionStorage.removeItem(ActiveOrgService.STORAGE_KEY);
    }
  }

  /** Clear the selection (e.g. on logout). */
  clear(): void {
    this.select(null);
  }

  private loadFromStorage(): number | null {
    const stored = sessionStorage.getItem(ActiveOrgService.STORAGE_KEY);
    return stored ? Number(stored) : null;
  }
}
